import { css, html, LitElement } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';

import * as THREE from 'three';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { Font, FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

/* ----------------------------- */
/*           TYPY                */
/* ----------------------------- */

interface LetterUserData {
    /**
     * Bazowa pozycja litery.
     * Na nią nakładane jest falowanie.
     */
    baseX: number;
    baseY: number;

    /**
     * Indywidualne przesunięcie fazy animacji.
     */
    phase: number;

    /**
     * Kierunek i prędkość swobodnego ruchu litery.
     */
    velocityX: number;
    velocityY: number;
}

/* ----------------------------- */
/*        KOMPONENT              */
/* ----------------------------- */

@customElement('logo-3d')
export class Logo3D extends LitElement {
    @property({ type: String })
    text = 'Arti24';

    @query('canvas')
    private canvas!: HTMLCanvasElement;

    private renderer?: THREE.WebGLRenderer;

    private readonly scene = new THREE.Scene();

    private readonly camera = new THREE.OrthographicCamera(-20, 20, 10, -10, 0.1, 200);

    private readonly group = new THREE.Group();

    private font?: Font;

    private frameId = 0;

    private previousFrameTime?: number;

    private resizeObserver?: ResizeObserver;

    /**
     * Pola ograniczające wykorzystywane przy kolizjach.
     */
    private readonly letterBoundingBox = new THREE.Box3();

    private readonly collisionBoxA = new THREE.Box3();

    private readonly collisionBoxB = new THREE.Box3();

    /**
     * Faktyczny rozmiar wygenerowanego napisu.
     * Wartości początkowe są używane do chwili załadowania fontu.
     */
    private logoWidth = 36;

    private logoHeight = 10;

    /**
     * Maksymalne wychylenie litery podczas falowania.
     */
    private readonly animationRadius = 1.2;

    /**
     * Zapas kamery wokół napisu.
     */
    private readonly cameraPadding = 1.02;

    /**
     * Odstęp litery od krawędzi obszaru renderowania.
     */
    private readonly wallPadding = 0.2;

    /**
     * Minimalny odstęp między literami.
     */
    private readonly letterCollisionPadding = 0.15;

    /**
     * Minimalne prędkości po zderzeniu.
     * Zapobiegają sklejaniu się liter.
     */
    private readonly minimumVelocityX = 0.08;

    private readonly minimumVelocityY = 0.04;

    static styles = css`
        :host {
            display: block;

            /*
         * Logo może być szersze niż wcześniej.
         * Kontener strony ma około 1296 px, więc 1100 px
         * pozostawia odpowiedni margines.
         */
            width: min(100%, 1100px);

            /*
         * Większa wysokość poprawia proporcje kamery
         * ortograficznej i sprawia, że litery zajmują
         * większą część szerokości płótna.
         */
            height: clamp(180px, 25vw, 300px);

            margin-inline: auto;
            overflow: hidden;
        }

        canvas {
            display: block;
            width: 100%;
            height: 100%;
        }

        @media (max-width: 480px) {
            :host {
                height: 170px;
            }
        }
    `;
    /* ----------------------------- */
    /*        LIFECYCLE              */
    /* ----------------------------- */

    firstUpdated(): void {
        this.initScene();
        this.initRenderer();
        this.observeResize();
        this.loadFont();
        this.startAnimation();
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();

        cancelAnimationFrame(this.frameId);

        this.previousFrameTime = undefined;

        this.resizeObserver?.disconnect();
        this.resizeObserver = undefined;

        this.disposeText();

        this.renderer?.dispose();
        this.renderer = undefined;
    }

    protected updated(changed: Map<string, unknown>): void {
        if (changed.has('text') && this.font) {
            this.refreshText();
        }
    }

    /* ----------------------------- */
    /*        INICJALIZACJA          */
    /* ----------------------------- */

    private initScene(): void {
        this.camera.position.set(0, 0, 60);
        this.camera.lookAt(0, 0, 0);

        this.scene.add(this.group);

        const ambientLight = new THREE.AmbientLight(0xffffff, 0.7);

        this.scene.add(ambientLight);

        const directionalLight = new THREE.DirectionalLight(0xffffff, 1);

        directionalLight.position.set(5, 10, 10);

        this.scene.add(directionalLight);
    }

    private initRenderer(): void {
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            alpha: true,
        });

        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        this.renderer.setClearColor(0x000000, 0);

        this.onResize();
    }

    private observeResize(): void {
        this.resizeObserver = new ResizeObserver(() => {
            this.onResize();
        });

        this.resizeObserver.observe(this);
    }

    /* ----------------------------- */
    /*        DOPASOWANIE KAMERY     */
    /* ----------------------------- */

    private onResize(): void {
        if (!this.renderer) {
            return;
        }

        const width = this.clientWidth;
        const height = this.clientHeight;

        if (!width || !height) {
            return;
        }

        this.renderer.setSize(width, height, false);

        const aspect = width / height;

        /*
         * Uwzględniamy pionowe poruszanie się liter.
         */
        const animatedLogoHeight = this.logoHeight + this.animationRadius * 2 + 0.5;

        /*
         * Wysokość kamery wymagana do pokazania całej
         * początkowej szerokości napisu.
         */
        const heightRequiredByWidth = (this.logoWidth * this.cameraPadding) / aspect;

        /*
         * Wysokość kamery wymagana do pokazania napisu
         * wraz z jego pionową animacją.
         */
        const heightRequiredByHeight = animatedLogoHeight * this.cameraPadding;

        const cameraHeight = Math.max(heightRequiredByWidth, heightRequiredByHeight);

        const cameraWidth = cameraHeight * aspect;

        this.camera.left = -cameraWidth / 2;
        this.camera.right = cameraWidth / 2;
        this.camera.top = cameraHeight / 2;
        this.camera.bottom = -cameraHeight / 2;

        this.camera.updateProjectionMatrix();

        /*
         * Po zmianie rozmiaru okna litery mogą znaleźć się
         * poza nowymi granicami.
         */
        this.resolveAllWallCollisions();
    }

    /* ----------------------------- */
    /*        FONT / TEKST           */
    /* ----------------------------- */

    private loadFont(): void {
        const loader = new FontLoader();

        loader.load(
            'https://threejs.org/examples/fonts/helvetiker_bold.typeface.json',

            (font) => {
                this.font = font;
                this.refreshText();
            },

            undefined,

            (error) => {
                console.error('[Logo3D] Nie udało się załadować fontu:', error);
            },
        );
    }

    private refreshText(): void {
        if (!this.font) {
            return;
        }

        this.disposeText();

        /*
         * Poprzednie położenie grupy nie może wpływać
         * na pomiar nowego napisu.
         */
        this.group.position.set(0, 0, 0);

        let x = 0;

        const spacing = 1.2;

        this.text.split('').forEach((char, index) => {
            const geometry = new TextGeometry(char, {
                font: this.font!,
                size: 8,
                depth: 2,

                bevelEnabled: true,
                bevelThickness: 0.3,
                bevelSize: 0.2,
                bevelSegments: 3,
            });

            geometry.computeBoundingBox();

            const boundingBox = geometry.boundingBox;

            if (!boundingBox) {
                geometry.dispose();
                return;
            }

            const characterWidth = boundingBox.max.x - boundingBox.min.x;

            const material = new THREE.MeshPhongMaterial({
                color: this.getColor(char),
                shininess: 120,
            });

            const mesh = new THREE.Mesh(geometry, material);

            /*
             * Zachowany wcześniejszy wygląd i sposób
             * ustawiania geometrii liter.
             */
            mesh.position.set(x, 0, 0);

            /*
             * Kolejne litery rozpoczynają ruch
             * w różnych kierunkach.
             *
             * Prędkości są dwukrotnie większe
             * niż w wolniejszej wersji.
             */
            const horizontalDirection = index % 2 === 0 ? 1 : -1;

            const verticalDirection = index % 3 === 0 ? 1 : -1;

            mesh.userData = {
                baseX: x,
                baseY: 0,

                phase: index * Math.PI * 0.35,

                velocityX: horizontalDirection * (0.11 + index * 0.012),

                velocityY: verticalDirection * (0.05 + index * 0.008),
            } satisfies LetterUserData;

            this.group.add(mesh);

            x += characterWidth + spacing;
        });

        const box = new THREE.Box3().setFromObject(this.group);

        if (box.isEmpty()) {
            return;
        }

        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());

        this.logoWidth = size.x;
        this.logoHeight = size.y;

        /*
         * Początkowe wyśrodkowanie napisu.
         */
        this.group.position.set(-center.x, -center.y, 0);

        this.onResize();
    }

    private disposeText(): void {
        for (const object of [...this.group.children]) {
            const mesh = object as THREE.Mesh;

            mesh.geometry?.dispose();

            if (Array.isArray(mesh.material)) {
                for (const material of mesh.material) {
                    material.dispose();
                }
            } else {
                mesh.material?.dispose();
            }

            this.group.remove(mesh);
        }

        this.group.clear();
    }

    private getColor(char: string): number {
        const colors: Record<string, number> = {
            2: 0xffd700,
            4: 0xaaaaaa,
            a: 0xff3b3b,
            r: 0x3b82f6,
            t: 0x22c55e,
            i: 0xa855f7,
        };

        return colors[char.toLowerCase()] ?? 0xcccccc;
    }

    /* ----------------------------- */
    /*       KOLIZJE ZE ŚCIANAMI     */
    /* ----------------------------- */

    private resolveAllWallCollisions(): void {
        for (const object of this.group.children) {
            const mesh = object as THREE.Mesh;

            const data = mesh.userData as Partial<LetterUserData>;

            if (
                typeof data.baseX !== 'number' ||
                typeof data.baseY !== 'number' ||
                typeof data.velocityX !== 'number' ||
                typeof data.velocityY !== 'number'
            ) {
                continue;
            }

            this.resolveWallCollision(mesh, data as LetterUserData);
        }
    }

    private resolveWallCollision(mesh: THREE.Mesh, data: LetterUserData): void {
        /*
         * Box3 uwzględnia aktualny obrót oraz położenie litery.
         */
        mesh.updateWorldMatrix(true, false);

        this.letterBoundingBox.setFromObject(mesh);

        const leftWall = this.camera.left + this.wallPadding;

        const rightWall = this.camera.right - this.wallPadding;

        const bottomWall = this.camera.bottom + this.wallPadding;

        const topWall = this.camera.top - this.wallPadding;

        /*
         * Lewa ściana.
         */
        if (this.letterBoundingBox.min.x < leftWall) {
            const correction = leftWall - this.letterBoundingBox.min.x;

            data.baseX += correction;
            mesh.position.x += correction;

            data.velocityX = Math.max(Math.abs(data.velocityX), this.minimumVelocityX);
        }

        /*
         * Prawa ściana.
         */
        if (this.letterBoundingBox.max.x > rightWall) {
            const correction = this.letterBoundingBox.max.x - rightWall;

            data.baseX -= correction;
            mesh.position.x -= correction;

            data.velocityX = -Math.max(Math.abs(data.velocityX), this.minimumVelocityX);
        }

        /*
         * Dolna ściana.
         */
        if (this.letterBoundingBox.min.y < bottomWall) {
            const correction = bottomWall - this.letterBoundingBox.min.y;

            data.baseY += correction;
            mesh.position.y += correction;

            data.velocityY = Math.max(Math.abs(data.velocityY), this.minimumVelocityY);
        }

        /*
         * Górna ściana.
         */
        if (this.letterBoundingBox.max.y > topWall) {
            const correction = this.letterBoundingBox.max.y - topWall;

            data.baseY -= correction;
            mesh.position.y -= correction;

            data.velocityY = -Math.max(Math.abs(data.velocityY), this.minimumVelocityY);
        }
    }

    /* ----------------------------- */
    /*       KOLIZJE MIĘDZY LITERAMI */
    /* ----------------------------- */

    private resolveInterLetterCollisions(): void {
        const meshes = this.group.children.filter((object): object is THREE.Mesh => object instanceof THREE.Mesh);

        /*
         * Kilka przebiegów pomaga rozdzielić litery,
         * kiedy zderza się jednocześnie więcej niż para.
         */
        for (let pass = 0; pass < 3; pass += 1) {
            for (let firstIndex = 0; firstIndex < meshes.length; firstIndex += 1) {
                for (let secondIndex = firstIndex + 1; secondIndex < meshes.length; secondIndex += 1) {
                    this.resolveLetterPairCollision(meshes[firstIndex], meshes[secondIndex]);
                }
            }
        }
    }

    private resolveLetterPairCollision(firstMesh: THREE.Mesh, secondMesh: THREE.Mesh): void {
        const firstData = firstMesh.userData as LetterUserData;

        const secondData = secondMesh.userData as LetterUserData;

        firstMesh.updateWorldMatrix(true, false);
        secondMesh.updateWorldMatrix(true, false);

        /*
         * Dodajemy połowę docelowego odstępu do każdego pola.
         * Łącznie tworzy to pełny odstęp między literami.
         */
        const padding = this.letterCollisionPadding / 2;

        this.collisionBoxA.setFromObject(firstMesh).expandByVector(new THREE.Vector3(padding, padding, 0));

        this.collisionBoxB.setFromObject(secondMesh).expandByVector(new THREE.Vector3(padding, padding, 0));

        const overlapX =
            Math.min(this.collisionBoxA.max.x, this.collisionBoxB.max.x) -
            Math.max(this.collisionBoxA.min.x, this.collisionBoxB.min.x);

        const overlapY =
            Math.min(this.collisionBoxA.max.y, this.collisionBoxB.max.y) -
            Math.max(this.collisionBoxA.min.y, this.collisionBoxB.min.y);

        /*
         * Brak kolizji w płaszczyźnie ekranu.
         */
        if (overlapX <= 0 || overlapY <= 0) {
            return;
        }

        const firstCenterX = (this.collisionBoxA.min.x + this.collisionBoxA.max.x) / 2;

        const secondCenterX = (this.collisionBoxB.min.x + this.collisionBoxB.max.x) / 2;

        const firstCenterY = (this.collisionBoxA.min.y + this.collisionBoxA.max.y) / 2;

        const secondCenterY = (this.collisionBoxB.min.y + this.collisionBoxB.max.y) / 2;

        /*
         * Rozdzielamy litery w osi, w której nachodzą
         * na siebie w mniejszym stopniu.
         */
        if (overlapX < overlapY) {
            this.resolveHorizontalLetterCollision(
                firstMesh,
                secondMesh,
                firstData,
                secondData,
                firstCenterX,
                secondCenterX,
                overlapX,
            );

            return;
        }

        this.resolveVerticalLetterCollision(
            firstMesh,
            secondMesh,
            firstData,
            secondData,
            firstCenterY,
            secondCenterY,
            overlapY,
        );
    }

    private resolveHorizontalLetterCollision(
        firstMesh: THREE.Mesh,
        secondMesh: THREE.Mesh,
        firstData: LetterUserData,
        secondData: LetterUserData,
        firstCenterX: number,
        secondCenterX: number,
        overlapX: number,
    ): void {
        /*
         * Każda litera zostaje przesunięta o połowę
         * wspólnego nachodzenia.
         */
        const correction = overlapX / 2 + 0.01;

        const firstSpeed = Math.max(Math.abs(firstData.velocityX), this.minimumVelocityX);

        const secondSpeed = Math.max(Math.abs(secondData.velocityX), this.minimumVelocityX);

        if (firstCenterX <= secondCenterX) {
            /*
             * Pierwsza litera znajduje się po lewej.
             */
            firstData.baseX -= correction;
            secondData.baseX += correction;

            firstMesh.position.x -= correction;
            secondMesh.position.x += correction;

            firstData.velocityX = -secondSpeed;
            secondData.velocityX = firstSpeed;

            return;
        }

        /*
         * Pierwsza litera znajduje się po prawej.
         */
        firstData.baseX += correction;
        secondData.baseX -= correction;

        firstMesh.position.x += correction;
        secondMesh.position.x -= correction;

        firstData.velocityX = secondSpeed;
        secondData.velocityX = -firstSpeed;
    }

    private resolveVerticalLetterCollision(
        firstMesh: THREE.Mesh,
        secondMesh: THREE.Mesh,
        firstData: LetterUserData,
        secondData: LetterUserData,
        firstCenterY: number,
        secondCenterY: number,
        overlapY: number,
    ): void {
        const correction = overlapY / 2 + 0.01;

        const firstSpeed = Math.max(Math.abs(firstData.velocityY), this.minimumVelocityY);

        const secondSpeed = Math.max(Math.abs(secondData.velocityY), this.minimumVelocityY);

        if (firstCenterY <= secondCenterY) {
            /*
             * Pierwsza litera znajduje się niżej.
             */
            firstData.baseY -= correction;
            secondData.baseY += correction;

            /*
             * Przesuwamy również aktualną pozycję mesha.
             * Dzięki temu zachowujemy nałożone falowanie.
             */
            firstMesh.position.y -= correction;
            secondMesh.position.y += correction;

            firstData.velocityY = -secondSpeed;
            secondData.velocityY = firstSpeed;

            return;
        }

        /*
         * Pierwsza litera znajduje się wyżej.
         */
        firstData.baseY += correction;
        secondData.baseY -= correction;

        firstMesh.position.y += correction;
        secondMesh.position.y -= correction;

        firstData.velocityY = secondSpeed;
        secondData.velocityY = -firstSpeed;
    }

    /* ----------------------------- */
    /*        ANIMACJA               */
    /* ----------------------------- */

    private startAnimation(): void {
        /*
         * Dwukrotnie szybsza wersja spokojnej animacji.
         */
        const animationSpeed = 0.00044;

        const animate = (timestamp: number): void => {
            this.frameId = requestAnimationFrame(animate);

            const previousFrameTime = this.previousFrameTime ?? timestamp;

            /*
             * Ograniczenie zapobiega dużemu skokowi animacji
             * po powrocie do nieaktywnej karty.
             */
            const deltaSeconds = Math.min((timestamp - previousFrameTime) / 1000, 0.05);

            this.previousFrameTime = timestamp;

            const time = timestamp * animationSpeed;

            const breath = 1 + Math.sin(time * 0.4) * 0.05;

            /*
             * Najpierw ustawiamy aktualne pozycje
             * i obroty wszystkich liter.
             */
            this.group.children.forEach((object) => {
                const mesh = object as THREE.Mesh;

                const data = mesh.userData as LetterUserData;

                data.baseX += data.velocityX * deltaSeconds;

                data.baseY += data.velocityY * deltaSeconds;

                mesh.position.x = data.baseX;

                mesh.position.y = data.baseY + Math.sin(time + data.phase) * this.animationRadius * breath;

                mesh.position.z = Math.cos(time + data.phase) * this.animationRadius * breath;

                mesh.rotation.y = (time + data.phase) * 0.35;
            });

            /*
             * Kolejność:
             *
             * 1. zabezpieczenie przed wyjściem poza ekran,
             * 2. odbicia między literami,
             * 3. ponowne sprawdzenie ścian po rozdzieleniu liter.
             */
            this.resolveAllWallCollisions();
            this.resolveInterLetterCollisions();
            this.resolveAllWallCollisions();

            this.renderer?.render(this.scene, this.camera);
        };

        this.frameId = requestAnimationFrame(animate);
    }

    /* ----------------------------- */
    /*        TEMPLATE               */
    /* ----------------------------- */

    render() {
        return html`
            <canvas aria-label="Animowane logo Arti24" role="img"></canvas>
        `;
    }
}
